<template>
  <!-- Stickerverwaltung (Trello 26.08.2026): Sticker anlegen/bearbeiten mit Farbe - das Eingabefeld im
       Lieferplan kann seitdem nur noch vorhandene Sticker auswaehlen. -->
  <modal class-name="record-tag-modal-container" @close="$emit('close')">
    <template slot="header">
      <h4 class="modal-title">{{ $t("shippingPlan.recordTagManager.title") }}</h4>
      <md-button class="md-simple md-just-icon md-round modal-default-button" @click="$emit('close')">
        <md-icon>clear</md-icon>
      </md-button>
    </template>
    <template slot="body">
      <div class="rtm-form">
        <div class="rtm-form__head">{{ editing ? $t("shippingPlan.recordTagManager.edit", { name: editing.name }) : $t("shippingPlan.recordTagManager.newTag") }}</div>
        <md-field :class="{ 'md-error': !!error }">
          <label>{{ $t("shippingPlan.recordTagManager.name") }}</label>
          <md-input v-model="name" maxlength="60" md-counter="false" @keyup.enter="save"></md-input>
          <span v-if="error" class="md-error">{{ error }}</span>
        </md-field>
        <div class="rtm-color">
          <span class="rtm-color__label">{{ $t("shippingPlan.recordTagManager.color") }}</span>
          <button
            v-for="c in presets"
            :key="c"
            type="button"
            class="rtm-color__swatch"
            :class="{ 'is-active': color.toLowerCase() === c }"
            :style="`background:${c}`"
            :title="c"
            @click="color = c"
          ></button>
          <label class="rtm-color__custom" :title="$t('shippingPlan.recordTagManager.customColor')">
            <input v-model="color" type="color" />
          </label>
          <md-chip class="rtm-preview" :style="chipStyle(color)">{{ name || $t("shippingPlan.recordTagManager.preview") }}</md-chip>
        </div>
        <div class="rtm-form__actions">
          <md-button class="md-secondary md-sm" @click="resetForm">{{ $t("shippingPlan.recordTagManager.cancel") }}</md-button>
          <md-button class="md-success md-sm" :disabled="saving || !name.trim()" @click="save">{{ $t("shippingPlan.recordTagManager.save") }}</md-button>
        </div>
      </div>

      <div class="rtm-list">
        <div v-for="t in sortedTags" :key="t.id" class="rtm-list__row" :class="{ 'is-editing': editing && editing.id === t.id }">
          <md-chip :style="chipStyle(t.color)">{{ t.name }}</md-chip>
          <md-button class="md-simple md-just-icon md-round md-sm" :title="$t('shippingPlan.recordTagManager.editTitle')" @click="startEdit(t)">
            <md-icon>edit</md-icon>
          </md-button>
        </div>
        <p v-if="!sortedTags.length" class="rtm-list__empty">{{ $t("shippingPlan.recordTagManager.empty") }}</p>
      </div>
    </template>
    <template slot="footer">
      <md-button class="md-secondary" @click="$emit('close')">{{ $t("shippingPlan.recordTagManager.close") }}</md-button>
    </template>
  </modal>
</template>

<script>
import { Modal } from "@/components";
import ShippingDataService from "@/services/shipping-data.service";

const PRESETS = ["#ff6347", "#1e88e5", "#7f915a", "#fb8c00", "#8e24aa", "#00acc1", "#6d4c41", "#546e7a"];

export default {
  name: "RecordTagManager",
  components: { Modal },
  data() {
    return {
      name: "",
      color: PRESETS[0],
      editing: null,
      saving: false,
      error: null,
      presets: PRESETS,
    };
  },
  computed: {
    sortedTags() {
      const all = this.$store.state.shippingData.recordTags || [];
      return [...all].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
  },
  methods: {
    chipStyle(color) {
      const bg = color || "darkgray";
      return `background-color: ${bg} !important; border: 2px solid ${bg}; color: ${this.contrastingTextColor(bg)} !important;`;
    },
    contrastingTextColor(hex) {
      if (!hex) return "#fff";
      const c = hex.replace("#", "");
      if (c.length !== 6) return "#fff";
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luma > 0.6 ? "#000" : "#fff";
    },
    startEdit(t) {
      this.editing = t;
      this.name = t.name;
      // Altbestand traegt "tomato" als Farbname - der Farbwaehler braucht Hex
      this.color = /^#/.test(t.color || "") ? t.color : PRESETS[0];
      this.error = null;
    },
    resetForm() {
      this.editing = null;
      this.name = "";
      this.color = PRESETS[0];
      this.error = null;
    },
    async save() {
      if (!this.name.trim() || this.saving) return;
      this.saving = true;
      this.error = null;
      try {
        const payload = { name: this.name.trim(), color: this.color };
        let oldName = null;
        if (this.editing) {
          oldName = this.editing.name;
          await ShippingDataService.updateRecordTag(this.editing.id, payload);
        } else {
          await ShippingDataService.createRecordTag(payload);
        }
        await this.$store.dispatch("shippingData/getRecordTags");
        this.$emit("changed", { oldName, newName: payload.name });
        this.resetForm();
      } catch (e) {
        const reason = e && e.response && e.response.data && e.response.data.reason;
        const key = "shippingPlan.recordTagManager.errors." + reason;
        this.error = this.$te(key) ? this.$t(key) : this.$t("shippingPlan.recordTagManager.errors.failed");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rtm-form {
  text-align: left;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 8px 14px 10px;
  margin-bottom: 14px;

  &__head {
    font-size: 13px;
    color: #666;
    margin-bottom: 2px;
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }
}
.rtm-color {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 8px;

  &__label {
    font-size: 12px;
    color: #666;
    margin-right: 4px;
  }
  &__swatch {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;

    &.is-active {
      border-color: #333;
      box-shadow: 0 0 0 2px #fff inset;
    }
  }
  &__custom input {
    width: 28px;
    height: 24px;
    padding: 0;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: none;
    cursor: pointer;
  }
}
.rtm-preview {
  margin-left: auto;
}
.rtm-list {
  text-align: left;
  max-height: 40vh;
  overflow-y: auto;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 4px;
    border-radius: 4px;

    &.is-editing {
      background: #f2f6eb;
    }
  }
  &__empty {
    color: #999;
    font-size: 13px;
  }
}
</style>
